import { useCallback } from 'react';
import { gql } from '@apollo/client';
import { isEmpty } from 'lodash';

import { useLazyQuery } from '@ion/api';
import permissions from '@ion/api/Apollo/permissions';

const SORTABLE_FIELDS = {
  name: 'name',
  writekey: 'writekey',
  cluster: 'cluster',
  integrations: 'integrations',
};

export const query = gql`
  query IonPipelinesList(
    $clusterIdFilter: pipelines_bool_exp!
    $searchTerm: String!
    $orderBy: [pipelines_order_by!]
    $integrationIdFilter: pipelines_bool_exp!
    $playbookIdFilter: pipelines_bool_exp!
    $tagIdFilter: pipelines_bool_exp!
  ) {
    pipelinesAggregate: pipelines_aggregate(
      where: {
        _and: [
          $clusterIdFilter
          { deletedAt: { _is_null: true } }
          { _or: [{ name: { _ilike: $searchTerm } }, { writekey: { _ilike: $searchTerm } }] }
          $integrationIdFilter
          $playbookIdFilter
          $tagIdFilter
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    pipelines(
      order_by: $orderBy
      where: {
        _and: [
          $clusterIdFilter
          { deletedAt: { _is_null: true } }
          { _or: [{ name: { _ilike: $searchTerm } }, { writekey: { _ilike: $searchTerm } }] }
          $integrationIdFilter
          $playbookIdFilter
          $tagIdFilter
        ]
      }
    ) {
      id
      name
      writekey
      cluster {
        id
        name
      }
      pipelineTags: pipeline_tags(order_by: { created_at: asc }) {
        id
        tag {
          id
          label
          type
        }
      }
      pipelineIntegrations: pipeline_integrations {
        id
        integrationId
        pipelineId
        disabled
        integration {
          id
          starterkitId
          name
        }
        consent: pipeline_integration_consent {
          id
          mode
          consentCategoryId
          category: consent_category {
            id
            code
          }
        }
        revision: staged_revision {
          id
          stagedForDelete
          pipelineVars
          playbook {
            revision
            pipelineVars: pipeline_variables {
              id
              name
            }
          }
        }
        pipeline {
          id
          name
        }
      }
    }
  }
`;

function buildOrderBy(sort) {
  switch (sort.field) {
    case SORTABLE_FIELDS.integrations: {
      return {
        pipeline_integrations_aggregate: { count: sort.direction },
      };
    }
    case SORTABLE_FIELDS.cluster: {
      return {
        cluster: { name: sort.direction },
      };
    }
    default: {
      return { [sort.field]: sort.direction };
    }
  }
}

export function buildVariables({ clusterIds, searchTerm = '', integrationIds, playbookIds, tagIds, sort }) {
  const clusterIdFilter = !isEmpty(clusterIds) ? { clusterId: { _in: clusterIds } } : {};
  const integrationIdFilter = !isEmpty(integrationIds)
    ? { pipeline_integrations: { integration: { id: { _in: integrationIds } } } }
    : {};
  const playbookIdFilter = !isEmpty(playbookIds)
    ? { pipeline_integrations: { staged_revision: { playbook: { id: { _in: playbookIds } } } } }
    : {};
  const tagIdFilter = !isEmpty(tagIds) ? { pipeline_tags: { tagId: { _in: tagIds } } } : {};

  return {
    searchTerm: `%${searchTerm}%`,
    clusterIdFilter,
    integrationIdFilter,
    playbookIdFilter,
    tagIdFilter,
    orderBy: buildOrderBy(sort),
  };
}

/**
 * Lazy query hook for fetching pipelines from Ion database only (no control proxy).
 * Returns a memoized fetch function to prevent infinite re-render loops.
 */
export default function useLazyQueryIonPipelines(options = {}) {
  const [executeQuery, { data, ...rest }] = useLazyQuery(query, {
    context: { permission: permissions.readPipeline },
    fetchPolicy: 'network-only',
    ...options,
  });

  const fetchPipelines = useCallback(
    ({ clusterIds, searchTerm, integrationIds, playbookIds, tagIds, sort }) => {
      const variables = buildVariables({ clusterIds, searchTerm, integrationIds, playbookIds, tagIds, sort });
      return executeQuery({ variables });
    },
    [executeQuery]
  );

  return [
    fetchPipelines,
    {
      pipelines: data?.pipelines || [],
      pagination: data?.pipelinesAggregate?.aggregate || { count: 0 },
      ...rest,
    },
  ];
}
